import {forwardRef, ReactNode, RefObject, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import styles from './styles/CleverSlider.module.scss';
import Button from "../Button";
import FlatIcon from "../FlatIcon";

interface CleverSlideContainerProps {
    children: ReactNode[];
    className?: string;
    trackClass?: string;
    currentIndex: number;
    padding?: number;
    style: { height: number | string };
    slideMap: RefObject<HTMLDivElement | null>[];
    events: Record<string, any>; // Replace 'any' with the appropriate type
    ref: RefObject<HTMLDivElement>;
    getMore?: () => void;
    scrollArrowClassName?: string;
    handleNext: () => void;
    handlePrev: () => void;
}

const CleverSlideContainer = forwardRef<HTMLDivElement, CleverSlideContainerProps>((props, ref: RefObject<HTMLDivElement>) => {
    const [trackWidth, setTrackWidth] = useState(Infinity);
    const [childWidths, setChildWidths] = useState<number[]>([]);

    useEffect(() => {
        const cWidths: number[] = [];
        let width = 0;
        props.slideMap?.forEach((value) => {
            const rect = value.current?.getBoundingClientRect();
            if (rect) {
                cWidths.push(rect.width);
                width += rect.width + (props.padding || 0);
            }
        });
        setTrackWidth(width);
        setChildWidths(cWidths);
    }, [props.slideMap]);

    const allowSlideRight = useMemo(() => {
        let totalWidthLeft = 0;
        if (childWidths.length === 0) return;
        for (let i = props.currentIndex; i < childWidths.length; i++) {
            totalWidthLeft += childWidths[i] + (props.padding || 0);
        }
        if (ref?.current?.clientWidth) {
            return Number(totalWidthLeft) > ref.current.clientWidth;
        }
    }, [props.currentIndex, childWidths.length]);

    const trackClassName = classNames({
        [styles.track]: true,
        [props.trackClass || '']: props.trackClass,
    });
    const trackContainerClassName = classNames({
        [styles.trackContainer]: true,
        [styles.sliderContainer]: true,
        [styles.sliderContainerShowGradientRight]: allowSlideRight && props.currentIndex === 0,
        [styles.sliderContainerShowGradientLeft]: props.currentIndex > 0 && !allowSlideRight,
        [styles.sliderContainerShowGradientBoth]: allowSlideRight && props.currentIndex > 0,
        [props.className || ""]: props.className,
    });
    const scrollArrowPrevClassName = classNames({
        [styles.scrollArrow]: true,
        [styles.scrollArrowPrev]: true,
        [props.scrollArrowClassName as string]: props.scrollArrowClassName,
    });
    const scrollArrowNextClassName = classNames({
        [styles.scrollArrow]: true,
        [styles.scrollArrowNext]: true,
        [props.scrollArrowClassName as string]: props.scrollArrowClassName,
    });

    let translate = 0;
    for (let i = 0; i < props.currentIndex; i++) {
        translate -= childWidths[i] + (props.padding || 0);
    }

    const sliderStyle = {transform: `translateX(${translate}px)`, width: `${trackWidth}px`, height: "100%"}

    return (
        <div>
            {props.currentIndex > 0 &&
                <Button aria-label={"Go To Previous Page"} type="button" className={scrollArrowPrevClassName}
                        onClick={props.handlePrev}
                        style={{transform: "rotate(180deg)"}}>
                    <FlatIcon name="flaticon-next"/>
                </Button>}
            <div ref={ref} className={trackContainerClassName} style={props.style} {...props.events}>
                <div
                    className={trackClassName}
                    style={sliderStyle}
                >
                    {props.children}
                </div>
            </div>
            {allowSlideRight &&
                <Button aria-label={"Go To Next Page"} type="button" className={scrollArrowNextClassName}
                        onClick={props.handleNext}>
                    <FlatIcon name="flaticon-next"/>
                </Button>}
        </div>
    )
});

CleverSlideContainer.displayName = 'CleverSlideContainer';

export default CleverSlideContainer;
